/**
 * Governed content calendar — approvable, reversible, ledgered scheduling.
 *
 * Fixes the audit's most damning Content Engine gap: "Schedule" used to be
 * UI theater with no store and no governance. This module makes scheduling
 * a GOVERNED write that honors ARGO's core invariant end-to-end:
 *
 *   • proposeSchedule NEVER publishes — it compiles an old→new actionDiff
 *     approval item (the same trust mechanic as approvalQueue) and hands it
 *     to an injected `enqueue`, leaving the piece `pending_approval`;
 *   • approveSchedule is the ONLY transition into `scheduled`, and it seals
 *     provenance (injected `seal`, wired to provenance.provenanceDigest in
 *     the UI) so every approved slot carries a tamper-evident sealId;
 *   • reverseSchedule compiles a compensating reversal record using the
 *     SAME marker contract as the reversal engine (REVERSAL_NOTE_PREFIX)
 *     and emits it through the injected `reverse` — undo is a first-class,
 *     recorded event, never a silent delete;
 *   • campaignMission fuses a multi-asset campaign into ONE missionQueue
 *     mission whose per-asset schedules each need their own approval;
 *   • attributeOutcome links published outcomes (replies/meetings) back to
 *     the source asset as a value-ledger entry for the ROI rollup.
 *
 * Deliberately PURE and store-free: schedule state is an in-module pure
 * store ({@link createScheduleStore}) or the caller's own array — nothing
 * here touches the db Store interface, firebase, or the clock. Best-time
 * slotting deliberately does NOT duplicate agentScheduler's cadence math:
 * that module answers "when does an agent re-run", this one answers "when
 * does a channel's audience look" — a per-channel audience-hour heuristic.
 */

import { fnvHash } from '../ledger/verifyChain';
import { REVERSAL_NOTE_PREFIX } from '../agent/reversal';
import type { MissionItem } from '../agent/missionQueue';

// ---------------------------------------------------------------------------
// Schema
// ---------------------------------------------------------------------------

/** Channels the calendar can slot content into. */
export type Channel = 'linkedin' | 'x' | 'email' | 'blog';

/** Lifecycle of a scheduled piece. `published` is unreachable without an
 *  approval: only `scheduled` (i.e. approved + sealed) pieces may publish. */
export type ScheduleStatus =
  | 'draft'
  | 'pending_approval'
  | 'approved'
  | 'scheduled'
  | 'published'
  | 'reversed';

/** One asset slotted (or proposed) on the calendar. */
export type ScheduledPiece = {
  id: string;
  assetId: string;
  channel: Channel;
  /** ms epoch the piece is slotted to go out. */
  publishAt: number;
  status: ScheduleStatus;
  /** Set by proposeSchedule — ties the piece to its approval item. */
  approvalId?: string;
  /** Set by approveSchedule — the provenance seal of the approved slot. */
  sealId?: string;
  /** Set by campaignMission — every piece of one campaign shares this. */
  missionId?: string;
  /** Set by reverseSchedule — id of the emitted reversal record. */
  reversalId?: string;
};

/** Minimal reference to a content asset (a draft from the Content Engine). */
export interface ContentAssetRef {
  id: string;
  title?: string;
  text?: string;
}

/** actionDiff-style approval item a schedule proposal routes for sign-off. */
export interface ScheduleApproval {
  id: string;
  pieceId: string;
  assetId: string;
  channel: Channel;
  publishAt: number;
  /** old→new field diff — the approvalQueue trust mechanic. */
  actionDiff: string;
  /** Human-readable "why this" note for the inbox row. */
  note: string;
  status: 'pending';
  createdAt: number;
}

/** Compensating record emitted when a scheduled/published piece is reversed. */
export interface ScheduleReversalRecord {
  id: string;
  reversesPieceId: string;
  /** Status the piece held when the reversal was compiled. */
  fromStatus: ScheduleStatus;
  /** Seal of the approval being compensated, when one existed. */
  sealId?: string;
  /** Parseable marker note — same contract as the reversal engine. */
  note: string;
}

/** Value-ledger entry linking a published outcome back to its asset. */
export interface ContentValueEntry {
  id: string;
  assetId: string;
  pieceId: string;
  channel: Channel;
  signals: { replies: number; meetings: number };
  /** Conservative operator-minutes estimate — auditable, never inflated. */
  minutesSaved: number;
  note: string;
}

// ---------------------------------------------------------------------------
// Best time to post — per-channel audience-hour heuristic
// ---------------------------------------------------------------------------

/**
 * Preferred send hour (UTC) per channel. Exported so the assumption is
 * visible and testable, mirroring valueLedger's MINUTES_SAVED_BY_KIND
 * honesty pattern. Deliberately distinct per channel: email lands before
 * the workday, LinkedIn at desk-settle, X at lunch scroll, blog mid-PM.
 */
export const BEST_HOUR_UTC: Record<Channel, number> = {
  email: 8,
  linkedin: 9,
  x: 12,
  blog: 15,
};

const DAY_MS = 24 * 60 * 60 * 1000;

/**
 * Next best slot for `channel` strictly after `now` (ms epoch). Pure and
 * deterministic: the next occurrence of the channel's audience hour, today
 * if still ahead, else tomorrow.
 */
export function bestTimeToPost(channel: Channel, now: number): number {
  const hour = BEST_HOUR_UTC[channel];
  const d = new Date(now);
  const todaySlot = Date.UTC(d.getUTCFullYear(), d.getUTCMonth(), d.getUTCDate(), hour);
  return todaySlot > now ? todaySlot : todaySlot + DAY_MS;
}

// ---------------------------------------------------------------------------
// Calendar view model + conflict detection
// ---------------------------------------------------------------------------

/** Calendar day key (UTC): 'YYYY-MM-DD'. */
export function dayKey(publishAt: number): string {
  return new Date(publishAt).toISOString().slice(0, 10);
}

/** Channel+hour slot key — two live pieces sharing one collide. */
function slotKey(p: ScheduledPiece): string {
  return `${p.channel}|${new Date(p.publishAt).toISOString().slice(0, 13)}`;
}

export interface ScheduleQueue {
  /** Pieces grouped by UTC day, each day sorted by publishAt ascending. */
  byDay: Record<string, ScheduledPiece[]>;
  /** Groups of ≥2 live pieces competing for the same channel/hour slot. */
  conflicts: ScheduledPiece[][];
}

/**
 * Calendar view model: group by day and detect slot collisions. Reversed
 * pieces still render (history is visible) but never conflict — a reversed
 * slot is free again. Pure; input untouched.
 */
export function buildQueue(pieces: ScheduledPiece[]): ScheduleQueue {
  const byDay: Record<string, ScheduledPiece[]> = {};
  const bySlot = new Map<string, ScheduledPiece[]>();

  for (const p of pieces) {
    const day = dayKey(p.publishAt);
    (byDay[day] ??= []).push(p);
    if (p.status !== 'reversed') {
      const key = slotKey(p);
      const group = bySlot.get(key) ?? [];
      group.push(p);
      bySlot.set(key, group);
    }
  }
  for (const day of Object.keys(byDay)) {
    byDay[day] = [...byDay[day]].sort((a, b) => a.publishAt - b.publishAt);
  }
  const conflicts = [...bySlot.values()].filter((g) => g.length >= 2);
  return { byDay, conflicts };
}

// ---------------------------------------------------------------------------
// Governed transitions
// ---------------------------------------------------------------------------

/** Deterministic short id from any payload — same FNV-1a family the ledger
 *  chain seals with, so a double-click compiles the same id. */
function idOf(prefix: string, payload: unknown): string {
  return `${prefix}-${fnvHash('', payload).replace(/^fnv1a:/, '')}`;
}

export interface ProposeDeps {
  /** ms epoch "now" — injected, never read from the clock. */
  now: number;
  /** Approval-queue sink (approvalQueue-style). The ONLY exit for a proposal. */
  enqueue: (approval: ScheduleApproval) => unknown;
}

/**
 * Propose slotting `asset` on `channel` at its best time. Routes an
 * actionDiff-style approval item through `deps.enqueue` and returns the
 * piece as `pending_approval`. NEVER publishes, NEVER auto-approves —
 * scheduling is a governed write.
 */
export function proposeSchedule(
  asset: ContentAssetRef,
  channel: Channel,
  deps: ProposeDeps,
): { piece: ScheduledPiece; approval: ScheduleApproval } {
  const publishAt = bestTimeToPost(channel, deps.now);
  const pieceId = idOf('piece', [asset.id, channel, publishAt]);
  const approvalId = idOf('sched-approval', [pieceId, publishAt]);
  const when = new Date(publishAt).toISOString();
  const label = asset.title ?? asset.id;

  const approval: ScheduleApproval = {
    id: approvalId,
    pieceId,
    assetId: asset.id,
    channel,
    publishAt,
    actionDiff: `status: draft → scheduled · publishAt: (none) → ${when} · channel: ${channel}`,
    note: `Schedule “${label}” on ${channel} at ${when} — publishes only after your approval.`,
    status: 'pending',
    createdAt: deps.now,
  };

  const piece: ScheduledPiece = {
    id: pieceId,
    assetId: asset.id,
    channel,
    publishAt,
    status: 'pending_approval',
    approvalId,
  };

  deps.enqueue(approval);
  return { piece, approval };
}

export interface ApproveDeps {
  /** Provenance seal (wire to provenance.buildProvenance + provenanceDigest). */
  seal: (payload: {
    pieceId: string;
    approvalId: string;
    assetId: string;
    channel: Channel;
    publishAt: number;
  }) => string;
}

/**
 * The ONLY transition into `scheduled`. Requires the piece to be pending
 * approval and the approval to be ITS approval — then seals provenance and
 * stamps the sealId. Throws on any attempt to skip the human gate.
 */
export function approveSchedule(
  piece: ScheduledPiece,
  approval: ScheduleApproval,
  deps: ApproveDeps,
): ScheduledPiece {
  if (piece.status !== 'pending_approval') {
    throw new Error(
      `Cannot approve piece ${piece.id} in status '${piece.status}' — only a ` +
        `pending_approval piece can be scheduled. Propose it first.`,
    );
  }
  if (piece.approvalId && approval.id !== piece.approvalId) {
    throw new Error(
      `Approval ${approval.id} does not match piece ${piece.id} (expects ${piece.approvalId}).`,
    );
  }
  const sealId = deps.seal({
    pieceId: piece.id,
    approvalId: approval.id,
    assetId: piece.assetId,
    channel: piece.channel,
    publishAt: piece.publishAt,
  });
  return { ...piece, status: 'scheduled', sealId };
}

export interface ReverseDeps {
  /** Reversal sink — receives the compiled compensating record. */
  reverse: (record: ScheduleReversalRecord) => unknown;
}

/**
 * Reverse a scheduled/published piece. Compiles a compensating record with
 * the SAME parseable marker contract as the reversal engine and emits it
 * through `deps.reverse` — undo is recorded, never silent. Drafts and
 * pending proposals have nothing governed to compensate, so they throw.
 */
export function reverseSchedule(
  piece: ScheduledPiece,
  deps: ReverseDeps,
): ScheduledPiece {
  if (piece.status !== 'scheduled' && piece.status !== 'published') {
    throw new Error(
      `Cannot reverse piece ${piece.id} in status '${piece.status}' — only ` +
        `scheduled or published pieces carry a governed write to compensate.`,
    );
  }
  const record: ScheduleReversalRecord = {
    id: `undo-${piece.id}`,
    reversesPieceId: piece.id,
    fromStatus: piece.status,
    ...(piece.sealId !== undefined ? { sealId: piece.sealId } : {}),
    note: `${REVERSAL_NOTE_PREFIX}${piece.id} — pull ${piece.channel} slot at ${new Date(piece.publishAt).toISOString()}`,
  };
  deps.reverse(record);
  return { ...piece, status: 'reversed', reversalId: record.id };
}

/**
 * Mark a sealed, scheduled piece as published. The ONLY transition into
 * `published`, and it is unreachable without approveSchedule having run —
 * the invariant the whole module exists to enforce.
 */
export function publishPiece(piece: ScheduledPiece): ScheduledPiece {
  if (piece.status !== 'scheduled') {
    throw new Error(
      `Cannot publish piece ${piece.id} in status '${piece.status}' — only an ` +
        `approved, sealed (scheduled) piece may publish.`,
    );
  }
  return { ...piece, status: 'published' };
}

// ---------------------------------------------------------------------------
// Campaign mission — one mission, N governed approvals
// ---------------------------------------------------------------------------

/**
 * Fuse a multi-asset campaign into ONE missionQueue mission. Each asset is
 * proposed on its channel via {@link proposeSchedule} — so every piece
 * lands `pending_approval` with its own enqueued approval item — and slots
 * are staggered one day apart per asset so a campaign never self-collides.
 */
export function campaignMission(
  brief: string,
  assets: { channel: Channel; asset: ContentAssetRef }[],
  deps: ProposeDeps,
): { missionId: string; pieces: ScheduledPiece[]; mission: MissionItem } {
  const missionId = idOf('mission', [brief, assets.map((a) => a.asset.id)]);

  const pieces: ScheduledPiece[] = assets.map(({ channel, asset }, i) => {
    const { piece } = proposeSchedule(asset, channel, {
      now: deps.now + i * DAY_MS, // stagger: one asset per day, best hour each
      enqueue: deps.enqueue,
    });
    return { ...piece, missionId };
  });

  const mission: MissionItem = {
    id: missionId,
    sourceAgent: 'contentEngine',
    title: `Campaign: ${brief}`,
    dollarsAtStake: 0,
    urgency: 0.5,
    confidence: 0.8,
    // Needs a human more than an auto-run: every piece gates on approval.
    autonomyTier: 1,
    proposalRef: missionId,
    evidence: pieces.map(
      (p) =>
        `${p.channel} slot ${new Date(p.publishAt).toISOString()} for asset ${p.assetId} — awaiting approval ${p.approvalId}`,
    ),
  };

  return { missionId, pieces, mission };
}

// ---------------------------------------------------------------------------
// Outcome attribution — ROI rollup entry
// ---------------------------------------------------------------------------

/** Conservative minutes-of-operator-value per outcome signal. Exported so
 *  the assumption is auditable, mirroring valueLedger's honesty rule. */
export const MINUTES_PER_SIGNAL = { reply: 2, meeting: 30 } as const;

export interface AttributeDeps {
  /** Value-ledger append sink (valueLedger-style entry recorder). */
  record: (entry: ContentValueEntry) => unknown;
}

/**
 * Link real published outcomes (replies, meetings) back to the source
 * asset as a value-ledger entry. Unrecorded signals count as 0 — unknown
 * is never assumed positive.
 */
export function attributeOutcome(
  piece: ScheduledPiece,
  signals: { replies?: number; meetings?: number },
  deps: AttributeDeps,
): { valueEntry: ContentValueEntry } {
  const replies = Math.max(0, signals.replies ?? 0);
  const meetings = Math.max(0, signals.meetings ?? 0);
  const minutesSaved =
    replies * MINUTES_PER_SIGNAL.reply + meetings * MINUTES_PER_SIGNAL.meeting;

  const valueEntry: ContentValueEntry = {
    id: idOf('content-value', [piece.id, replies, meetings]),
    assetId: piece.assetId,
    pieceId: piece.id,
    channel: piece.channel,
    signals: { replies, meetings },
    minutesSaved,
    note: `${piece.channel} piece drove ${replies} repl${replies === 1 ? 'y' : 'ies'}, ${meetings} meeting${meetings === 1 ? '' : 's'} — attributed to asset ${piece.assetId}.`,
  };
  deps.record(valueEntry);
  return { valueEntry };
}

// ---------------------------------------------------------------------------
// In-module pure store — deterministic state the UI passes via deps
// ---------------------------------------------------------------------------

/** Pure upsert: replace by id or append. Returns a new array. */
export function upsertPiece(
  pieces: ScheduledPiece[],
  piece: ScheduledPiece,
): ScheduledPiece[] {
  const idx = pieces.findIndex((p) => p.id === piece.id);
  if (idx < 0) return [...pieces, piece];
  return [...pieces.slice(0, idx), piece, ...pieces.slice(idx + 1)];
}

export interface ScheduleStore {
  list(): ScheduledPiece[];
  get(id: string): ScheduledPiece | undefined;
  upsert(piece: ScheduledPiece): ScheduledPiece;
}

/**
 * Deterministic in-module store — NOT the db Store interface, by design.
 * Holds calendar state for callers (tests, the view) that want a stable
 * handle instead of threading arrays.
 */
export function createScheduleStore(initial: ScheduledPiece[] = []): ScheduleStore {
  let pieces: ScheduledPiece[] = [...initial];
  return {
    list: () => [...pieces],
    get: (id) => pieces.find((p) => p.id === id),
    upsert: (piece) => {
      pieces = upsertPiece(pieces, piece);
      return piece;
    },
  };
}
